///////////////////////////////////////////////////////////////////////////////
// Shared chrome + helpers for the new web app (home, /recharge, /checkout).
///////////////////////////////////////////////////////////////////////////////
import React from 'react';
import { marketPathForIso, marketForPath } from './markets';

const buildVersion = require('../../config/version.js');
const { COUNTRY_SLUGS, SLUG_BY_ISO } = require('../../lib/countrySlugs');
// Cache-buster for assets under the unversioned /st/b bucket.
export const BUILD_VERSION = buildVersion;

const _cdnHost = (typeof window !== 'undefined' && window.__MT && window.__MT.cdnHost) || '';
export const ST = _cdnHost ? `https://${_cdnHost}/st/b` : '/st/b';

// Carrier logo manifest — local files beat remote CDN URLs.
// Loaded once; the manifest is generated by scripts/pull-carrier-logos.js.
let _logoManifest = null;
let _logoBase = null; // secondary index: lowercase base name → entry
let _logoBrand = null; // tertiary index: brandKey() → entry, for supplier renames
let _logoManifestLoading = false;
function loadLogoManifest() {
  if (_logoManifest || _logoManifestLoading) return;
  _logoManifestLoading = true;
  const _v = buildVersion && buildVersion !== 'dev' ? `?v=${encodeURIComponent(buildVersion)}` : '';
  fetch(`${_cdnHost ? `https://${_cdnHost}` : ''}/st/images/carriers/manifest.json${_v}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((m) => {
      _logoManifest = m || {};
      _manifestWaiters.forEach((fn) => fn());
      _manifestWaiters = [];
      // Build a base-name index: "Telcel Mexico" → indexed under "telcel"
      _logoBase = {};
      for (const [k, v] of Object.entries(_logoManifest)) {
        const base = k.toLowerCase().replace(/\s+(mexico|united kingdom|england|usa|philippines|canada|australia|new zealand|brazil|india|germany|france|spain|italy|south korea|japan|singapore|vietnam|indonesia|myanmar|thailand|argentina|chile|colombia|peru|ecuador|uruguay|panama|el salvador|dominican republic|costa rica|guatemala|honduras|bolivia|paraguay|bahamas|barbados|belize|bermuda|jamaica|guyana|trinidad|nigeria|kenya|south africa|egypt|saudi arabia|uae|turkey|pakistan|bangladesh|cambodia|laos|malaysia|china|russia|uk|us|nz|au|gb|ph|th|in|de|fr|es|it|kr|jp|sg|vn|id|mm|mx|br|ca|ar|co|pe|cl|ng|ke|za|eg|sa|ae|tr|bd|pk|kh|la|my)(\s+(bundles?|data|pin|promo|prepaid plans?))?$/i, '').trim();
        if (base && !_logoBase[base]) _logoBase[base] = v;
      }
      // Third index, keyed the same way the colour lookup keys things, so a
      // brand the supplier renamed ("Tri Telecom" for Three Indonesia) still
      // finds the art we hold. Consulted last, and only when the supplier gave
      // us no logo of its own.
      _logoBrand = {};
      for (const [k, v] of Object.entries(_logoManifest)) {
        const bk = brandKey(k);
        if (bk && !_logoBrand[bk]) _logoBrand[bk] = v;
      }
    })
    .catch(() => { _logoManifest = {}; _logoBase = {}; _logoBrand = {}; });
}

function resolveEntry(entry) {
  if (!entry) return null;
  if (entry.file) return `${_cdnHost ? `https://${_cdnHost}` : ''}/st/images/carriers/${entry.file}`;
  if (entry.url) return entry.url;
  return null;
}

/**
 * A carrier's own brand colours, derived from its logo by
 * scripts/carrier-logo-colors.py. `main` is a background, `accent` reads on it.
 */
// The manifest arrives after first paint. Anything that draws from it needs to
// redraw when it lands, or the page keeps the fallback it rendered with — which
// is how every carrier tile came out house-teal instead of its own colour.
let _manifestWaiters = [];

export function useLogoManifest() {
  const [, bump] = React.useState(0);
  React.useEffect(() => {
    if (_logoManifest) return undefined;
    let live = true;
    const fn = () => { if (live) bump((n) => n + 1); };
    _manifestWaiters.push(fn);
    loadLogoManifest();
    return () => { live = false; _manifestWaiters = _manifestWaiters.filter((f) => f !== fn); };
  }, []);
  return !!_logoManifest;
}

// Descriptors suppliers bolt onto a brand: "Verizon Wireless USA",
// "Cricket Paygo USA (Operator Service Fee Applied)", "T-Mobile (PIN) USA".
const BRAND_NOISE = /\s*\(.*?\)|\s+(rtr|pins?|paygo|cash card|gsm plan|gsm|unlimited monthly|unlimited nationwide|unlimited|nationwide|family plan|monthly|30-day|data|minutes|lifeline|prepaid plans?|prepaid|wireless|mobile|bundles?|promo|regular recharge|internet|paquete)\b.*$/gi;

const COUNTRY_WORDS = /\s+(usa|us|uk|gb|england|scotland|wales|united kingdom|united states|mexico|canada|philippines|australia|new zealand|germany|france|spain|italy|czech republic|belgium|switzerland|netherlands|india|indonesia|thailand|vietnam|japan|south korea|singapore|malaysia|myanmar|brazil|argentina|chile|colombia|peru|nigeria|kenya|south africa)$/i;

// Suppliers disagree on what an operator is called, so the same brand can miss
// artwork we already hold. Only equivalences that are verifiable from the names
// themselves belong here — showing the wrong operator's logo is worse than
// showing initials.
const BRAND_ALIASES = {
  // Reloadly "Tri Telecom (Formerly Three Telecom)" is Ding's "Hutchison Three
  // Indonesia" — the name says so, and both are the Indonesian Three.
  'tri telecom': 'hutchison three',
};

// "Verizon Wireless USA" -> "verizon"
function brandKey(name) {
  let n = String(name || '').trim();
  n = n.replace(COUNTRY_WORDS, '');
  n = n.replace(BRAND_NOISE, '');
  const key = n.replace(COUNTRY_WORDS, '').trim().toLowerCase();
  return BRAND_ALIASES[key] || key;
}

// Which country a manifest key belongs to. The parenthetical has to come off
// first — "Boost Mobile USA (Operator Service Fee Applied)" ends in a bracket,
// so an end-anchored test never sees the USA and the tile fell back to Boost
// Australia's colours.
function countryWord(name) {
  const cleaned = String(name || '').replace(/\s*\(.*?\)\s*$/, '').trim();
  const m = cleaned.match(COUNTRY_WORDS);
  return m ? m[1].toLowerCase() : null;
}

const ISO_WORDS = {
  US: ['usa', 'us', 'united states'],
  GB: ['united kingdom', 'england', 'uk', 'gb', 'scotland', 'wales'],
  MX: ['mexico'], CA: ['canada'], PH: ['philippines'], AU: ['australia'],
  NZ: ['new zealand'], DE: ['germany'], FR: ['france'], ES: ['spain'],
  IT: ['italy'], IN: ['india'], ID: ['indonesia'], TH: ['thailand'],
  VN: ['vietnam'], JP: ['japan'], KR: ['south korea'], SG: ['singapore'],
  MY: ['malaysia'], MM: ['myanmar'], BR: ['brazil'],
};

/**
 * A carrier's own brand colours, derived from its logo by
 * scripts/carrier-logo-colors.py. `main` is a background, `accent` reads on it.
 *
 * Country-scoped: without an ISO, "AT&T" matched AT&T *Mexico* and "T-Mobile"
 * matched T-Mobile *Czech Republic* — whichever the index happened to hold
 * first — so US tiles wore other countries' colours.
 */
export function carrierColors(name, iso) {
  if (!_logoManifest) { loadLogoManifest(); return null; }
  const exact = _logoManifest[String(name || '').trim()];
  if (exact && exact.main) return { main: exact.main, accent: exact.accent || '#fff' };

  const key = brandKey(name);
  if (!key) return null;
  const wanted = ISO_WORDS[String(iso || '').toUpperCase()] || [];
  let loose = null;
  for (const [k, v] of Object.entries(_logoManifest)) {
    if (!v || !v.main || brandKey(k) !== key) continue;
    const cw = countryWord(k);
    if (cw && wanted.includes(cw)) return { main: v.main, accent: v.accent || '#fff' };
    if (!loose) loose = v;
  }
  return loose ? { main: loose.main, accent: loose.accent || '#fff' } : null;
}


/**
 * Resolve the best logo URL for a carrier name.
 * Tries exact match, then base name (without country suffix).
 */
export function carrierLogo(name, apiUrl) {
  if (!_logoManifest) { loadLogoManifest(); return apiUrl || null; }
  const key = (name || '').trim();
  // Exact match
  const exact = resolveEntry(_logoManifest[key]);
  if (exact) return exact;
  // Base name match (e.g. "Telcel" matches "Telcel Mexico")
  if (_logoBase) {
    const base = key.toLowerCase().replace(/\s+(bundles?|data|pin|promo|prepaid plans?|regular recharge|internet|paquete)$/i, '').replace(/\s*[\-\/].*$/, '').trim();
    const fuzzy = resolveEntry(_logoBase[base]);
    if (fuzzy) return fuzzy;
  }
  // Last resort: never prefer a brandKey guess over the supplier's own URL.
  if (!apiUrl && _logoBrand) {
    const brand = resolveEntry(_logoBrand[brandKey(key)]);
    if (brand) return brand;
  }
  return apiUrl || null;
}

export const PAYMENT_LOGOS = [
  { file: 'visa.png', name: 'Visa' },
  { file: 'master.png', name: 'Mastercard' },
  { file: 'jcb.png', name: 'JCB' },
  { file: 'amex.png', name: 'American Express' },
  { file: 'alipay.png', name: 'Alipay' },
  { file: 'kbank.png', name: 'KBank' },
  { file: 'scb.png', name: 'SCB' },
];

export const PHP_PAYMENT_LOGOS = [
  { file: 'grabpay-logo.svg', name: 'GrabPay' },
  { file: 'paymaya-logo.svg', name: 'Maya' },
  { file: 'shopeepay-logo.svg', name: 'ShopeePay' },
  { file: 'bpi-logo.svg', name: 'BPI' },
  { file: 'ubp-logo.svg', name: 'UnionBank' },
  { file: 'rcbc-logo.svg', name: 'RCBC' },
  { file: 'chinabank-logo.svg', name: 'Chinabank' },
];

let regionNames = null;
try {
  regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
} catch (e) { /* very old browsers: fall back to ISO codes */ }

export function countryName(code) {
  try {
    const n = regionNames && regionNames.of(code);
    return n && n !== code ? n : code;
  } catch (e) {
    return code;
  }
}

export function flagEmoji(code) {
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

///////////////////////////////////////////////////////////////////////////////
// Display-currency conversion. Catalog prices are USD; when the visitor picks
// THB/PHP in the top bar, fmtPrice converts using the openexchangerates feed
// (/api/currencies). Falls back to USD until rates arrive.
///////////////////////////////////////////////////////////////////////////////
let FX_RATES = null; // { THB: 36.1, PHP: 56.4, ... } — USD base
let FX_DISPLAY = 'USD';
const FX_SYMBOL = { THB: '฿', PHP: '₱', EUR: '€', GBP: '£' };

export function setDisplayFx(currency) {
  FX_DISPLAY = currency || 'USD';
}

export function fetchFxRates(onReady) {
  if (FX_RATES) { if (onReady) onReady(); return; }
  fetch('/api/currencies')
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      if (d && d.status === 'ok' && d.data && typeof d.data === 'object') {
        FX_RATES = d.data;
        if (onReady) onReady();
      }
    })
    .catch(() => { /* prices stay in USD */ });
}

// Changes whenever converted prices would change — put it in useMemo deps
// when the memo bakes formatted price strings.
export function fxStamp() {
  return `${FX_DISPLAY}:${FX_RATES ? 1 : 0}`;
}

export function fmtPrice(n) {
  const rate = FX_DISPLAY !== 'USD' && FX_RATES && FX_RATES[FX_DISPLAY];
  if (rate) {
    const v = n * rate;
    // Whole numbers read better for THB/PHP-sized figures; EUR/GBP are USD-sized
    // and always show cents so €104.00 never sits next to €99.50.
    const wholeOk = FX_DISPLAY === 'THB' || FX_DISPLAY === 'PHP';
    const text = wholeOk && v >= 100
      ? Math.round(v).toLocaleString()
      : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${FX_SYMBOL[FX_DISPLAY] || ''}${text}`;
  }
  return `$${n.toFixed(2)}`;
}

export function fmtBaht(n) {
  return `฿${Math.round(Number(n || 0)).toLocaleString()}`;
}

// Money in the currency the customer is being quoted in. Falls back to the
// plain symbol when Intl doesn't know the locale/currency pair.
export function fmtMoney(n, currency, symbol) {
  const v = Number(n || 0);
  if (!currency || currency === 'THB') return `฿${Math.round(v).toLocaleString()}`;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, currencyDisplay: 'narrowSymbol',
    }).format(v);
  } catch (e) {
    return `${symbol || ''}${v.toFixed(2)}`;
  }
}

// Stable per-browser id the payment routes require (16–32 chars).
export function bfp() {
  try {
    let v = localStorage.getItem('mt_bfp');
    if (!v || (v.length !== 16 && v.length !== 32)) {
      v = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
      localStorage.setItem('mt_bfp', v);
    }
    return v;
  } catch (e) {
    return 'web0000000000000000';
  }
}


///////////////////////////////////////////////////////////////////////////////
// Display currency — what prices are *shown* in, everywhere on the site.
// Separate from the checkout currency, which decides what is actually charged.
// Detection (geo/IP) can seed this by writing mt_currency before the app boots.
///////////////////////////////////////////////////////////////////////////////
export const CURRENCIES = [
  { code: 'THB', symbol: '\u0e3f', name: 'Thai Baht' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'PHP', symbol: '\u20b1', name: 'Philippine Peso' },
  { code: 'EUR', symbol: '\u20ac', name: 'Euro' },
  { code: 'GBP', symbol: '\u00a3', name: 'British Pound' },
];

const CURRENCY_KEY = 'mt_currency';

export function readCurrency(me) {
  try {
    const saved = localStorage.getItem(CURRENCY_KEY);
    if (saved && CURRENCIES.some((c) => c.code === saved)) return saved;
  } catch (e) { /* private mode */ }
  if (me && me.currency && CURRENCIES.some((c) => c.code === me.currency)) return me.currency;
  // Otherwise take what the server detected — never guess a second time here.
  if (CURRENCIES.some((c) => c.code === SERVER_LOCALE.currency)) return SERVER_LOCALE.currency;
  return 'THB';
}

// True when nobody chose this currency — it was inferred. The UI can say so.
export function currencyWasGuessed() {
  const from = SERVER_LOCALE.source && SERVER_LOCALE.source.currency;
  return from === 'geo' || from === 'language' || from === 'default';
}

// Keeps the browser copy and, when signed in, the account preference in step.
export function useDisplayCurrency(me) {
  const [currency, setCurrencyState] = React.useState(() => readCurrency(null));
  const [, setFxTick] = React.useState(0);

  // fmtPrice reads module state — keep it in step and re-render when the
  // rates arrive so already-painted prices convert.
  setDisplayFx(currency);
  React.useEffect(() => {
    if (currency !== 'USD') fetchFxRates(() => setFxTick((t) => t + 1));
  }, [currency]);

  // An account preference wins once we know who this is, unless the visitor
  // already picked something in this browser.
  React.useEffect(() => {
    if (!me || !me.currency) return;
    let local = null;
    try { local = localStorage.getItem(CURRENCY_KEY); } catch (e) { /* ignore */ }
    if (!local) setCurrencyState(me.currency);
  }, [me && me.currency]);

  function setCurrency(code) {
    setCurrencyState(code);
    try { localStorage.setItem(CURRENCY_KEY, code); } catch (e) { /* ignore */ }
    writeLocaleCookie({ currency: code });
    if (me) {
      fetch('/api/checkout/currency', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currency: code }),
      }).catch(() => { /* the browser copy is enough */ });
    }
  }

  return [currency, setCurrency];
}

///////////////////////////////////////////////////////////////////////////////
// Country continuity — the last country the visitor picked on any catalog
// page (eSIM home, /recharge, /gifts), so switching pages stays on it.
///////////////////////////////////////////////////////////////////////////////
const COUNTRY_KEY = 'mt_country_pref';

export function readCountryPref() {
  try {
    const v = localStorage.getItem(COUNTRY_KEY);
    return v && /^[A-Z]{2}$/.test(v) ? v : null;
  } catch (e) { return null; }
}

export function writeCountryPref(code) {
  if (!code || !/^[A-Z]{2}$/.test(code)) return;
  try { localStorage.setItem(COUNTRY_KEY, code); } catch (e) { /* private mode */ }
}

// The visitor's deliberate "default location", chosen in Preferences > Country.
// Separate from mt_country_pref above, which also follows browsing (any catalog
// page rewrites it). A cookie, so the server can see it: lib/marketRedirect.js
// sends them to that country's market home (/uk, /us) instead of guessing from
// their IP. Functional cookie, listed in the cookie policy.
export function writeCountryChoice(code) {
  if (!code || !/^[A-Z]{2}$/.test(code)) return;
  try {
    const secure = window.location.protocol === 'https:' ? ';secure' : '';
    document.cookie = `mt_country_choice=${code};path=/;max-age=${365 * 86400};samesite=lax${secure}`;
  } catch (e) { /* cookies blocked */ }
}

// Countries the visitor searched/picked — chip personalization source 4
const SEARCHED_KEY = 'mt_searched_countries';

export function readSearchedCountries() {
  try {
    const v = JSON.parse(localStorage.getItem(SEARCHED_KEY) || '[]');
    return Array.isArray(v) ? v.filter((c) => /^[A-Z]{2}$/.test(c)) : [];
  } catch (e) { return []; }
}

export function saveSearchedCountry(code) {
  if (!code || !/^[A-Z]{2}$/.test(code)) return;
  try {
    const list = [code, ...readSearchedCountries().filter((c) => c !== code)].slice(0, 6);
    localStorage.setItem(SEARCHED_KEY, JSON.stringify(list));
  } catch (e) { /* private mode */ }
}

///////////////////////////////////////////////////////////////////////////////
// Personalized country chips for signed-in visitors, in priority order:
//   1. the country they're visiting from (server geo, window.__MT.locale)
//   2. countries they hold topped-up numbers in (dial-prefix match)
//   3. countries saved in their account settings (me.savedCountries)
//   4. countries they've searched or picked before
// Guests get [] — pages fall back to their default pinned chips.
///////////////////////////////////////////////////////////////////////////////
export function useMyCountries(me) {
  const [phoneCountries, setPhoneCountries] = React.useState([]);

  React.useEffect(() => {
    if (!me) { setPhoneCountries([]); return; }
    let dead = false;
    Promise.all([
      fetch('/api/phonenumbers').then((r) => (r.ok ? r.json() : null)),
      fetch('/api/topup/countries').then((r) => (r.ok ? r.json() : null)),
    ]).then(([phones, countries]) => {
      if (dead || !phones || !countries || countries.status !== 'ok') return;
      const dials = (countries.data || [])
        .map((c) => ({
          iso: c.CountryIso,
          dial: (c.InternationalDialingInformation && c.InternationalDialingInformation[0] && c.InternationalDialingInformation[0].Prefix) || null,
        }))
        .filter((c) => c.dial && /^[A-Z]{2}$/.test(c.iso || ''))
        .sort((a, b) => b.dial.length - a.dial.length); // longest prefix wins
      const found = [];
      for (const n of (phones.phoneNumbers || [])) {
        if (n.isRemoved) continue;
        const digits = String(n.number || '').replace(/\D/g, '');
        const hit = dials.find((d) => digits.startsWith(d.dial));
        if (hit && !found.includes(hit.iso)) found.push(hit.iso);
      }
      setPhoneCountries(found);
    }).catch(() => { /* chips fall back to defaults */ });
    return () => { dead = true; };
  }, [me && me.id]);

  return React.useMemo(() => {
    if (!me) return [];
    const geo = (SERVER_LOCALE.country || '').toUpperCase();
    const saved = Array.isArray(me.savedCountries) ? me.savedCountries : [];
    const merged = [];
    for (const c of [geo, ...phoneCountries, ...saved, ...readSearchedCountries()]) {
      if (c && /^[A-Z]{2}$/.test(c) && !merged.includes(c)) merged.push(c);
    }
    return merged.slice(0, 8);
  }, [me, phoneCountries]);
}

///////////////////////////////////////////////////////////////////////////////
// Recent phone numbers — stored locally for autocomplete, never sent anywhere
// until the visitor submits them again.
///////////////////////////////////////////////////////////////////////////////
const PHONES_KEY = 'mt_recent_phones';

export function readRecentPhones() {
  try {
    const v = JSON.parse(localStorage.getItem(PHONES_KEY) || '[]');
    return Array.isArray(v) ? v.filter((p) => typeof p === 'string') : [];
  } catch (e) { return []; }
}

export function saveRecentPhone(e164) {
  if (!e164 || String(e164).replace(/\D/g, '').length < 6) return;
  try {
    const list = [e164, ...readRecentPhones().filter((p) => p !== e164)].slice(0, 5);
    localStorage.setItem(PHONES_KEY, JSON.stringify(list));
  } catch (e) { /* private mode */ }
}

///////////////////////////////////////////////////////////////////////////////
// Lightweight event tracking — fire-and-forget POST to /api/track.
// Batches nothing; each call is a single fetch. The server stores these in the
// user_events table for preference computation.
///////////////////////////////////////////////////////////////////////////////
export function track(event, data = {}) {
  try {
    const body = { event, ...data };
    fetch('/api/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    }).catch(() => {}); // swallow errors — tracking must never break UX
  } catch (e) { /* */ }
}

///////////////////////////////////////////////////////////////////////////////
// Language — shares the legacy app's `lang` cookie (lib/i18n.coffee), so a
// choice made here translates every legacy page immediately.
///////////////////////////////////////////////////////////////////////////////
const _translations = {
  en: require('../../locales/en.js'),
  th: require('../../locales/th.js'),
  ru: require('../../locales/ru.js'),
  cn: require('../../locales/cn.js'),
  es: require('../../locales/es.js'),
  jp: require('../../locales/jp.js'),
  ko: require('../../locales/ko.js'),
};

export function useT() {
  const lang = readLang();
  return (key) => {
    const dict = _translations[lang] || _translations.en;
    return dict[key] || _translations.en[key] || key;
  };
}

export const LANGS = [
  { code: 'en', name: 'EN' },
  { code: 'th', name: 'ภาษาไทย' },
  { code: 'ru', name: 'Русский' },
  { code: 'cn', name: '中國' },
  { code: 'es', name: 'Español' },
  { code: 'jp', name: '日本語' },
  { code: 'ko', name: '한국어' },
];

// What the server resolved for this request (lib/middleware-locale.js).
// Detection already happened there — the client's job is to respect it and to
// let the visitor override it.
export const SERVER_LOCALE = (typeof window !== 'undefined' && window.__MT && window.__MT.locale)
  || { lang: 'en', currency: 'THB', country: null, source: {} };

export function readLang() {
  try {
    const q = new URLSearchParams(window.location.search).get('l');
    if (q && LANGS.some((l) => l.code === q)) return q;
    const m = document.cookie.match(/(?:^|;\s*)lang=([a-z]{2})/);
    if (m && LANGS.some((l) => l.code === m[1])) return m[1];
  } catch (e) { /* SSR / private mode */ }
  // Accept-Language / geo already folded in server-side
  if (LANGS.some((l) => l.code === SERVER_LOCALE.lang)) return SERVER_LOCALE.lang;
  return 'en';
}

// Mirrors the mt_locale cookie lib/middleware-locale.js reads, so the server
// resolves the same answer on the next request.
export function writeLocaleCookie(patch) {
  try {
    const current = { lang: readLang(), currency: readCurrency(null) };
    const next = Object.assign(current, patch);
    const value = encodeURIComponent(JSON.stringify({ lang: next.lang, currency: next.currency }));
    document.cookie = `mt_locale=${value};path=/;max-age=${365 * 86400};samesite=lax`;
  } catch (e) { /* ignore */ }
}

// `to`: where to land afterwards instead of reloading this page (a country change
// that also opens that country's market home).
export function setLang(code, to) {
  // Same cookie name, path and ~999-day lifetime as the legacy i18n module
  document.cookie = `lang=${code};path=/;max-age=${999 * 86400}`;
  writeLocaleCookie({ lang: code });
  // ?l= beats the cookie (readLang, middleware-locale), so a URL carrying it
  // would reload in the old language — move the parameter to the new one.
  if (to) { window.location.assign(to); return; }
  const url = new URL(window.location.href);
  if (url.searchParams.has('l')) {
    url.searchParams.set('l', code);
    window.location.replace(url.toString());
    return;
  }
  window.location.reload();
}

///////////////////////////////////////////////////////////////////////////////
// Preferences — single header trigger (flag + language/currency) opening a
// modal with Country/Language/Payment currency rows. Country reuses the
// existing mt_country_pref continuity cookie (readCountryPref/writeCountryPref)
// so it stays in step with the per-page pickers on /gifts, /recharge, etc.
///////////////////////////////////////////////////////////////////////////////
const ALL_COUNTRIES = Object.values(COUNTRY_SLUGS)
  .map((c) => ({ code: c.iso, name: c.name }))
  .sort((a, b) => a.name.localeCompare(b.name));

function PreferencesModal({ currency, onCurrency, onClose }) {
  const [view, setView] = React.useState('menu'); // 'menu' | 'country' | 'lang' | 'currency'
  const [q, setQ] = React.useState('');
  const [draftCountry, setDraftCountry] = React.useState(() => readCountryPref() || (SERVER_LOCALE.country || '').toUpperCase() || null);
  const [draftLang, setDraftLang] = React.useState(readLang);
  const [draftCurrency, setDraftCurrency] = React.useState(currency);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (view === 'country' && inputRef.current) inputRef.current.focus();
  }, [view]);

  React.useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape') return;
      if (view !== 'menu') setView('menu'); else onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [view, onClose]);

  function save() {
    const countryChanged = draftCountry && draftCountry !== readCountryPref();
    const langChanged = draftLang !== readLang();
    const currencyChanged = draftCurrency !== currency;
    if (countryChanged) { writeCountryPref(draftCountry); writeCountryChoice(draftCountry); }
    if (currencyChanged) onCurrency(draftCurrency);
    // A country with its own market home (UK, US) opens it. Leaving a market home
    // for a country without one goes to the main page instead of staying on the
    // old market's page.
    const marketTarget = countryChanged ? marketPathForIso(draftCountry) : null;
    const leaveMarket = countryChanged && !marketTarget && !!marketForPath(window.location.pathname);
    const to = marketTarget || (leaveMarket ? '/' : undefined);
    if (langChanged) { setLang(draftLang, to); return; } // writes cookie + reloads
    if (countryChanged) { if (to) window.location.assign(to); else window.location.reload(); return; }
    onClose();
  }

  const filteredCountries = React.useMemo(() => {
    const f = q.trim().toLowerCase();
    if (!f) return ALL_COUNTRIES;
    return ALL_COUNTRIES.filter((c) => c.name.toLowerCase().includes(f) || c.code.toLowerCase() === f);
  }, [q]);

  const langObj = LANGS.find((l) => l.code === draftLang) || LANGS[0];
  const currencyObj = CURRENCIES.find((c) => c.code === draftCurrency) || CURRENCIES[0];
  const titles = { menu: 'Preferences', country: 'Country', lang: 'Language', currency: 'Payment currency' };

  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal prefs-modal" role="dialog" aria-modal="true" aria-label="Preferences">
        <div className="modal-head">
          {view !== 'menu' && (
            <button type="button" className="prefs-back" onClick={() => setView('menu')} aria-label="Back to preferences">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
            </button>
          )}
          <div className="modal-title">{titles[view]}</div>
          <button type="button" className="modal-x" onClick={onClose} aria-label="Close">&#10005;</button>
        </div>

        <div className="modal-body">
          {view === 'menu' && (
            <div className="prefs-list">
              <button type="button" className="prefs-row" onClick={() => setView('country')}>
                <span className="prefs-row-icon">{draftCountry ? flagEmoji(draftCountry) : '🌐'}</span>
                <span className="prefs-row-label">Country</span>
                <span className="prefs-row-val">{draftCountry ? countryName(draftCountry) : 'Not set'}</span>
                <span className="prefs-row-chev" aria-hidden="true">›</span>
              </button>
              <button type="button" className="prefs-row" onClick={() => setView('lang')}>
                <span className="prefs-row-label">Language</span>
                <span className="prefs-row-val">{langObj.name}</span>
                <span className="prefs-row-chev" aria-hidden="true">›</span>
              </button>
              <button type="button" className="prefs-row" onClick={() => setView('currency')}>
                <span className="prefs-row-label">Payment currency</span>
                <span className="prefs-row-val">{currencyObj.symbol} {currencyObj.code}</span>
                <span className="prefs-row-chev" aria-hidden="true">›</span>
              </button>
            </div>
          )}

          {view === 'country' && (
            <>
              <input ref={inputRef} className="input" type="text" value={q}
                onChange={(e) => setQ(e.target.value)} placeholder="Search countries…" />
              <div className="prefs-sublist">
                {filteredCountries.map((c) => (
                  <button type="button" key={c.code} className={`prefs-row${draftCountry === c.code ? ' on' : ''}`}
                    onClick={() => { setDraftCountry(c.code); setView('menu'); }}>
                    <span className="prefs-row-icon">{flagEmoji(c.code)}</span>
                    <span className="prefs-row-label">{c.name}</span>
                    {draftCountry === c.code && <span className="prefs-row-check" aria-hidden="true">✓</span>}
                  </button>
                ))}
                {filteredCountries.length === 0 && <div className="picker-empty">No countries match "{q}"</div>}
              </div>
            </>
          )}

          {view === 'lang' && (
            <div className="prefs-sublist">
              {LANGS.map((l) => (
                <button type="button" key={l.code} className={`prefs-row${draftLang === l.code ? ' on' : ''}`}
                  onClick={() => { setDraftLang(l.code); setView('menu'); }}>
                  <span className="prefs-row-label">{l.name}</span>
                  {draftLang === l.code && <span className="prefs-row-check" aria-hidden="true">✓</span>}
                </button>
              ))}
            </div>
          )}

          {view === 'currency' && (
            <div className="prefs-sublist">
              {CURRENCIES.map((c) => (
                <button type="button" key={c.code} className={`prefs-row${draftCurrency === c.code ? ' on' : ''}`}
                  onClick={() => { setDraftCurrency(c.code); setView('menu'); }}>
                  <span className="prefs-row-label">{c.symbol} {c.code} — {c.name}</span>
                  {draftCurrency === c.code && <span className="prefs-row-check" aria-hidden="true">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {view === 'menu' && (
          <div className="modal-foot">
            <button type="button" className="btn block" onClick={save}>Save</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function PreferencesTrigger({ currency, onCurrency }) {
  const [open, setOpen] = React.useState(false);
  const country = readCountryPref() || (SERVER_LOCALE.country || '').toUpperCase();
  const lang = readLang();
  const langObj = LANGS.find((l) => l.code === lang) || LANGS[0];
  return (
    <>
      <button type="button" className="chip onbar prefs-trigger" onClick={() => setOpen(true)}
        aria-label={`Preferences — country, language and currency (currently ${langObj.name}, ${currency})`}>
        {/^[A-Z]{2}$/.test(country) && <span className="prefs-flag" aria-hidden="true">{flagEmoji(country)}</span>}
        {langObj.name} / {currency}
      </button>
      {open && <PreferencesModal currency={currency} onCurrency={onCurrency} onClose={() => setOpen(false)} />}
    </>
  );
}

function UserAvatar({ me, size = 28 }) {
  const initials = ((me.email || '?')[0] || '?').toUpperCase();
  if (me.picture) {
    return <img className="user-avatar" src={me.picture} alt="" width={size} height={size} referrerPolicy="no-referrer" />;
  }
  return <span className="user-avatar user-avatar-initials">{initials}</span>;
}

// Shared by the desktop UserDropdown and the mobile full-page menu, so
// switching between them (≤700px) doesn't refire the balance fetch.
function useUsdBalance(me) {
  const [usdBal, setUsdBal] = React.useState(null);
  React.useEffect(() => {
    if (!me || !me.hasUsdWallet) return;
    fetch('/api/wallet/balances')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setUsdBal(d.usd || 0); })
      .catch(() => {});
  }, [me && me.hasUsdWallet]);
  return usdBal;
}

// Header "Log in" link: come back to the current page after sign-in, except
// from the auth pages themselves (avoids /login?returnTo=/login loops).
function loginHref() {
  if (typeof window === 'undefined') return '/login?returnTo=%2F';
  const { pathname, search } = window.location;
  if (/^\/(login|signup|reset)/.test(pathname)) return '/login?returnTo=%2F';
  return `/login?returnTo=${encodeURIComponent(pathname + search)}`;
}

// The account nav links — same list, same icons, same order for the desktop
// dropdown and the mobile full-page menu, so the two can't drift apart.
function accountLinks(t, { thb, usd, hasBalance }) {
  return [
    { href: '/orders', label: t('nav_my_orders'), icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 12l2 2 4-4" /></svg> },
    {
      href: '/wallet',
      label: (
        <>
          {t('menu_link_balance')}
          {hasBalance && <span className="udrop-bal">{thb > 0 && <span>{fmtBaht(thb)}</span>}{usd > 0 && <span>${usd.toFixed(2)}</span>}</span>}
        </>
      ),
      icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 010-4h12v4" /><path d="M4 6v12a2 2 0 002 2h14v-4" /><path d="M18 12a2 2 0 000 4h4v-4z" /></svg>,
    },
    { href: '/top-me-up', label: 'Top Me Up', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 4.6a5.5 5.5 0 00-7.8 0L12 5.6l-1-1a5.5 5.5 0 00-7.8 7.8l1 1L12 21l7.8-7.8 1-1a5.5 5.5 0 000-7.8z" /></svg> },
    { href: '/account/referrals', label: 'Refer a friend', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v10H4V12" /><path d="M2 7h20v5H2z" /><path d="M12 22V7" /><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" /></svg> },
    { href: '/numbers', label: t('menu_link_phonenumbers'), icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></svg> },
    { href: '/settings', label: t('nav_settings'), icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg> },
    { href: '/support', label: t('footer_support'), icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg> },
  ];
}

const LOGOUT_ICON = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>;

function UserDropdown({ me, usdBal }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const t = useT();

  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const thb = Number(me.availableBalance || 0);
  const usd = usdBal !== null ? Number(usdBal) : 0;
  const hasBalance = thb > 0 || usd > 0;

  return (
    <div className="user-drop" ref={ref}>
      <button type="button" className="chip onbar user-chip" onClick={() => setOpen((o) => !o)}>
        <UserAvatar me={me} /> ▾
      </button>
      {open && (
        <div className="udrop-menu">
          <div className="udrop-email">{me.email}</div>
          {accountLinks(t, { thb, usd, hasBalance }).map((l) => (
            <a key={l.href} href={l.href}>{l.icon} {l.label}</a>
          ))}
          <a href="/api/logout">{LOGOUT_ICON} {t('menu_link_logout')}</a>
        </div>
      )}
    </div>
  );
}

// ≤700px account row — an avatar chip that just reveals the signed-in email
// on tap (no nested menu: every link it used to hold now lives in the flat
// list below, so there's nothing left to click through to).
function MobileAccountRow({ me }) {
  const [show, setShow] = React.useState(false);
  return (
    <div className="mmenu-account">
      <button type="button" className="mmenu-account-btn" onClick={() => setShow((s) => !s)} aria-expanded={show}>
        <UserAvatar me={me} size={32} />
        <span className={`mmenu-caret${show ? ' open' : ''}`} aria-hidden="true">▾</span>
      </button>
      {show && <div className="mmenu-email">{me.email}</div>}
    </div>
  );
}

// ≤700px full-page nav — replaces the old small dropdown-on-a-dropdown (the
// account UserDropdown nested inside the hamburger panel, which positioned
// itself for a desktop anchor and rendered off-screen/overlapping on mobile).
// Every account link that used to live behind a second tap now sits flat in
// this one list.
function MobileMenuPanel({ me, active, currency, onCurrency, usdBal, t }) {
  const thb = Number((me && me.availableBalance) || 0);
  const usd = usdBal !== null ? Number(usdBal) : 0;
  const hasBalance = thb > 0 || usd > 0;

  return (
    <div className="mobile-menu-full">
      {me && <MobileAccountRow me={me} />}
      <nav className="mmenu-nav-full">
        <a className={active === 'esim' ? 'on' : ''} href="/">{t('nav_esim_data')}</a>
        <a className={active === 'recharge' ? 'on' : ''} href="/recharge">{t('nav_mobile_topup')}</a>
        <a className={active === 'games' ? 'on' : ''} href="/gifts">{t('nav_gifts')}</a>
        {me && (
          <>
            <div className="mmenu-divider" />
            {accountLinks(t, { thb, usd, hasBalance }).map((l) => (
              <a key={l.href} href={l.href}>{l.icon} {l.label}</a>
            ))}
          </>
        )}
      </nav>
      <div className="mmenu-footer">
        {currency && onCurrency && <PreferencesTrigger currency={currency} onCurrency={onCurrency} />}
        {me
          ? <a href="/api/logout" className="mmenu-logout">{LOGOUT_ICON} {t('menu_link_logout')}</a>
          : <a href={loginHref()} className="btn block">{t('nav_login')}</a>}
      </div>
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// Breadcrumb — Home / <section switcher> / <country, opens the picker>.
// One shared component so every product page's hero bar carries it the same
// way. The section segment switches product (eSIM/Mobile Topup/Gifts), not
// country — the country segment is the country control, delegated back to
// the page via onCountryClick since each page already owns its own picker
// open/close state and CountryPicker instance.
///////////////////////////////////////////////////////////////////////////////
const NAV_SECTIONS = [
  { key: 'esim', label: 'eSIM Data' },
  { key: 'recharge', label: 'Mobile Topup' },
  { key: 'gifts', label: 'Gifts, Games & Utils' },
];

function sectionHref(key, iso) {
  const slug = iso && SLUG_BY_ISO[iso];
  if (key === 'esim') return slug ? `/esim/${slug}` : '/';
  if (key === 'recharge') return slug ? `/topup/${slug}` : (iso ? `/recharge?country=${iso}` : '/recharge');
  if (key === 'gifts') return slug ? `/gifts/${slug}` : (iso ? `/gifts?country=${iso}` : '/gifts');
  return '/';
}

export function Breadcrumb({ active, iso, countryName, onCountryClick }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = NAV_SECTIONS.find((s) => s.key === active) || NAV_SECTIONS[1];

  return (
    <nav className="mt-breadcrumb" aria-label="Breadcrumb">
      <a href="/">Home</a>
      <span aria-hidden="true"> › </span>
      <span className="mt-crumb-anchor" ref={ref}>
        <button type="button" onClick={() => setOpen((o) => !o)}>{current.label}</button>
        {open && (
          <div className="mt-crumb-menu">
            {NAV_SECTIONS.map((s) => (
              <a key={s.key} href={sectionHref(s.key, iso)} className={s.key === active ? 'on' : ''}>{s.label}</a>
            ))}
          </div>
        )}
      </span>
      {countryName && (
        <>
          <span aria-hidden="true"> › </span>
          {onCountryClick
            ? <button type="button" onClick={onCountryClick} aria-current="page">{countryName}</button>
            : <span aria-current="page">{countryName}</span>}
        </>
      )}
    </nav>
  );
}

export function Header({ me, active, currency, onCurrency, homeHref }) {
  // ≤700px the hamburger opens a full-page menu (MobileMenuPanel) instead of
  // reusing these desktop fragments — merging account links flat into one
  // list rather than nesting UserDropdown's anchored-for-desktop popup inside
  // it, which used to render off-position/overlapping on mobile.
  const [menuOpen, setMenuOpen] = React.useState(false);
  const t = useT();
  const usdBal = useUsdBalance(me);

  const navLinks = (
    <>
      <a className={active === 'esim' ? 'on' : ''} href="/">{t('nav_esim_data')}</a>
      <a className={active === 'recharge' ? 'on' : ''} href="/recharge">{t('nav_mobile_topup')}</a>
      <a className={active === 'games' ? 'on' : ''} href="/gifts">{t('nav_gifts')}</a>
      {me && me.paidOrders > 0 && <a href="/orders">{t('nav_my_orders')}</a>}
    </>
  );
  const account = (
    <>
      {currency && onCurrency && <PreferencesTrigger currency={currency} onCurrency={onCurrency} />}
      {me ? (
        <UserDropdown me={me} usdBal={usdBal} />
      ) : (
        <a className="chip onbar" href={loginHref()}>{t('nav_login')}</a>
      )}
    </>
  );

  return (
    <header className={`topbar${menuOpen ? ' menu-open' : ''}`}>
      <a href={homeHref || '/'} className="logo"><img src={`${ST}/images/2/logo_x2.png`} alt="MobileTopup.com" /></a>
      <nav className="nav">{navLinks}</nav>
      <div className="account">{account}</div>
      <button
        type="button" className="menu-btn"
        aria-label={menuOpen ? 'Close menu' : 'Menu'} aria-expanded={menuOpen}
        onClick={() => setMenuOpen((o) => !o)}
      >
        {menuOpen
          ? <svg width="22" height="22" viewBox="0 0 20 20" fill="none"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          : <svg width="22" height="22" viewBox="0 0 20 20" fill="none"><path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>}
      </button>
      {menuOpen && (
        <MobileMenuPanel me={me} active={active} currency={currency} onCurrency={onCurrency} usdBal={usdBal} t={t} />
      )}
    </header>
  );
}

// Support agents (Eve): WhatsApp, Telegram and web chat. Icons match the
// payment-icon size (22px) so the rail reads as one system.
export const SUPPORT_CHANNELS = [
  {
    name: 'WhatsApp',
    href: 'https://wa.me/66926200610?text=Hi',
    icon: (
      <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="24" cy="24" r="24" fill="#25D366" />
        <path d="M34.6 13.3A14.4 14.4 0 0 0 24 9.5c-8 0-14.5 6.5-14.5 14.5 0 2.6.7 5 1.9 7.2L9.5 38.5l7.5-2a14.4 14.4 0 0 0 7 1.8c8 0 14.5-6.5 14.5-14.5 0-3.9-1.5-7.5-4.2-10.2l.3-.3ZM24 35.8a12 12 0 0 1-6.1-1.7l-.4-.3-4.5 1.2 1.2-4.3-.3-.5A12 12 0 1 1 24 35.8Zm6.6-9c-.4-.2-2.2-1.1-2.5-1.2-.3-.1-.6-.2-.8.2s-1 1.2-1.2 1.5c-.2.2-.4.3-.8.1a10 10 0 0 1-5-4.4c-.4-.6.4-.6 1-2 .1-.2 0-.4 0-.6-.1-.2-.8-2-1.1-2.8-.3-.7-.6-.6-.8-.6h-.7a1.4 1.4 0 0 0-1 .5 4.2 4.2 0 0 0-1.3 3.1c0 1.8 1.3 3.6 1.5 3.9.2.2 2.6 4 6.3 5.6a21 21 0 0 0 2.1.8 5 5 0 0 0 2.3.2c.7-.1 2.2-.9 2.5-1.8.3-.9.3-1.6.2-1.8-.1-.2-.3-.3-.7-.4Z" fill="#fff" />
      </svg>
    ),
  },
  {
    name: 'Telegram',
    href: 'https://t.me/mobiletopup_bot',
    icon: (
      <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="24" cy="24" r="24" fill="#229ED9" />
        <path d="M33.6 14.2 29.9 33c-.3 1.2-.9 1.5-1.9 1l-5.3-3.9-2.5 2.5c-.3.3-.5.5-1 .5l.4-5.4 9.8-8.9c.4-.4-.1-.6-.6-.2l-12.1 7.6-5.2-1.6c-1.1-.4-1.2-1.1.2-1.7l20.4-7.9c1-.3 1.8.2 1.5 1.7v-.5Z" fill="#fff" />
      </svg>
    ),
  },
  {
    name: 'Messenger',
    href: 'https://m.me/thaiprepaidcard',
    icon: (
      <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="24" cy="24" r="24" fill="#0084FF" />
        <path d="M24 10c-8.3 0-14 5.9-14 13.5 0 4.2 1.7 7.6 4.5 10V38l4.3-2.4c1.2.3 2.4.5 3.7.5h1.5C32.3 36.1 38 30.2 38 23.5S32.3 10 24 10Zm1.4 18.2-3.6-3.8-7 3.8 7.6-8.1 3.6 3.8 7-3.8-7.6 8.1Z" fill="#fff" />
      </svg>
    ),
  },
];

export function SupportCard() {
  return (
    <div className="railcard trust">
      <h4>Support agents</h4>
      Talk to our AI support agent any time — order status, topups, eSIMs.
      <div className="supporticons">
        {SUPPORT_CHANNELS.map((c) => (
          <a key={c.name} href={c.href} target="_blank" rel="noopener noreferrer" title={c.name} aria-label={c.name}>
            {c.icon}
          </a>
        ))}
      </div>
      Answers in seconds, 24/7.
    </div>
  );
}

// KBank and SCB are Thai bank rails — they only make sense on a Baht page.
const THAI_ONLY_PAYMENTS = ['kbank.png', 'scb.png'];

export function PaymentCard({ currency }) {
  const cur = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];
  const logos = cur.code === 'THB'
    ? PAYMENT_LOGOS
    : PAYMENT_LOGOS.filter((l) => !THAI_ONLY_PAYMENTS.includes(l.file));
  return (
    <div className="railcard trust">
      <h4>Payment methods</h4>
      {cur.code === 'EUR' || cur.code === 'GBP'
        ? <><b>Shown in {cur.name}.</b> Prices are approximate. Your card is charged in Thai baht or US dollars and your bank applies its own exchange rate.</>
        : <><b>Pay in {cur.name}.</b> The price you see is the price charged.</>}
      <div className="paylogos">
        {logos.map((l) => (
          <img key={l.file} src={`${ST}/images/payment-icons/${l.file}`} alt={l.name} title={l.name} />
        ))}
      </div>
      No subscriptions, no upsells.
    </div>
  );
}
